/** * Read more about form validation here: https://react-hook-form.com/get-started#Applyvalidation ***/
import { checkIsSemanticVersion, isValidUrl } from "@via/utils/helpers.tsx";
import { AWS_ROLE_ARN_RE } from "common";

export const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
export const noSpaceRegex = /\s/;
export const noUppercaseRegex = /[A-Z]/;
export const httpsRegex = /^https:\/\/.+/;

const VMETA_URL_SAFE_NAME_REGEX = /^[A-Za-z0-9 _-]+$/;
const JSON_SAFE_KEY_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;
const VMETA_RESERVED_NAMES = new Set(["__proto__", "prototype", "constructor"]);

export const vmetaNameValidation = (fieldLabel = "Name") => ({
  required: `${fieldLabel} is required`,
  pattern: {
    value: VMETA_URL_SAFE_NAME_REGEX,
    message: `${fieldLabel} may contain only letters, numbers, spaces, "_" or "-". Other characters (including ".") are not allowed.`,
  },
  validate: {
    reserved: (value: string) => {
      if (!value) return true;
      return !VMETA_RESERVED_NAMES.has(value) || `${fieldLabel} is reserved.`;
    },
  },
});

export const jsonKeyValidation = (fieldLabel = "Field") => ({
  validate: {
    format: (value: string) => {
      if (!value) return true;
      return (
        JSON_SAFE_KEY_REGEX.test(value) ||
        `${fieldLabel} must start with a letter or "_" and contain only letters, numbers, and "_". Spaces and special characters are not allowed.`
      );
    },
    reserved: (value: string) => {
      if (!value) return true;
      return !VMETA_RESERVED_NAMES.has(value) || `${fieldLabel} is reserved.`;
    },
  },
});

export const requiredValidation = (
  fieldName = "",
  includeWhiteSpace = true,
  message = "This field is required"
) => {
  if (includeWhiteSpace) {
    return {
      required: fieldName ? `${fieldName} is required` : message,
      validate: whiteSpaceValidation(),
    };
  }
  return {
    required: fieldName ? `${fieldName} is required` : message,
  };
};

export const minLengthValidation = (length: number, message: string) => ({
  minLength: {
    value: length,
    message: message,
  },
});

export const maxLengthValidation = (length: number, message: string) => ({
  maxLength: {
    value: length,
    message: message,
  },
});

const minValueValidation =
  (min: number, message: string) => (value: string | number) => {
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numericValue)) return message;
    return numericValue >= min || message;
  };

const maxValueValidation =
  (max: number, message: string) => (value: string | number) => {
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numericValue)) return message;
    return numericValue <= max || message;
  };

const patternValidation = (pattern: RegExp, message: string) => ({
  pattern: {
    value: pattern,
    message: message,
  },
});

const containsSpaceValidation =
  (message = "Input cannot contain spaces") =>
  (value: string) =>
    /\s/.test(value?.trim()) ? message : true;

const whiteSpaceValidation =
  (message = "Input cannot be just spaces") =>
  (value: string) =>
    /^ +$/.test(value) ? message : true;

const startsWithNumberValidation =
  (message = "Input cannot start with a number") =>
  (value: string) =>
    /^\s*\d/.test(value) ? message : true;

const semanticVersionValidation =
  (message = "Version must be semantic (x.x.x) or (x.x)") =>
  (value: string) =>
    checkIsSemanticVersion(value, true) ? true : message;

const combineValidations =
  (...validators: ((value: string) => true | string)[]) =>
  (value: string) => {
    for (const validate of validators) {
      const result = validate(value);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };

export const signUpOptions: any = {
  email: {
    ...requiredValidation("Email"),
    ...patternValidation(emailRegex, "Invalid email format"),
  },
  name: {
    ...requiredValidation("Name"),
  },
  password: {
    ...requiredValidation("Password"),
    ...minLengthValidation(8, "Password must have at least 8 characters"),
  },
  confirmPassword: {
    ...requiredValidation("", true, "Confirming your password is required"),
  },
};

export const loginOptions: any = {
  username: {
    ...requiredValidation("Email"),
  },
  password: {
    ...requiredValidation("Password"),
    ...minLengthValidation(8, "Password must have at least 8 characters"),
  },
};

export const displayNameOptions: any = {
  label: {
    ...requiredValidation("Display Name"),
    validate: combineValidations(
      whiteSpaceValidation(),
      startsWithNumberValidation()
    ),
  },
};

export const outputParamOptions: any = {
  label: {
    ...requiredValidation("Display Name"),
  },
};

export const forgotPasswordOptions: any = {
  email: {
    ...requiredValidation("Email"),
    ...patternValidation(emailRegex, "Invalid email format"),
  },
};

export const resetPasswordOptions: any = {
  password: {
    ...requiredValidation("Password"),
    ...minLengthValidation(8, "Password must have at least 8 characters"),
  },
  confirmPassword: {
    ...requiredValidation("", true, "Confirming your password is required"),
  },
};

export const createProjectOptions: any = {
  name: {
    ...requiredValidation("Project Name"),
  },
  groupId: {
    ...requiredValidation("Group"),
  },
};

export const createWorkspaceOptions: any = {
  name: {
    ...requiredValidation("Workspace Name"),
  },
  groupId: {
    ...requiredValidation("Group"),
  },
};

export const createAppOptions: any = {
  name: {
    ...requiredValidation("App name"),
  },
  image: {
    ...requiredValidation("Container Image"),
  },
  groupId: {
    ...requiredValidation("Group"),
  },
  version: {
    ...requiredValidation("Version"),
    validate: semanticVersionValidation(),
  },
};

export const createCollectionOptions: any = {
  name: {
    ...requiredValidation("Collection name"),
  },
};

export const createWorkspaceDatasetOptions: any = {
  projectId: {
    ...requiredValidation("Project"),
  },
  datasetId: {
    ...requiredValidation("Dataset"),
  },
  description: {
    ...requiredValidation("Description"),
    ...maxLengthValidation(64, "Description must be less than 64 characters"),
  },
};

export const fileOrganizerFormOptions: any = {
  collectionId: {
    ...requiredValidation("Dateset"),
  },
};

export const createRunOptions: any = {
  name: {
    ...requiredValidation("Run name"),
  },
  projectId: {
    ...requiredValidation("Project"),
  },
  pipelineId: {
    ...requiredValidation("Pipeline"),
  },
};

export const createPipelineOptions: any = {
  menuGroup: {
    ...requiredValidation("Menu Group"),
  },
  name: {
    ...requiredValidation("Pipeline Name"),
  },
};

export const importPipelineOptions: any = {
  repositoryType: {
    ...requiredValidation("Repository Type"),
  },
  serviceTokenId: {
    ...requiredValidation("Git Account"),
  },
  repositoryUrl: {
    ...requiredValidation("Repository URL"),
    ...patternValidation(httpsRegex, "Please enter a valid HTTPS URL"),
  },
  pipelineType: {
    ...requiredValidation("Pipeline Type"),
  },
};

export const exportPipelineOptions: any = {
  repositoryType: {
    ...requiredValidation("Repository Type"),
  },
  serviceTokenId: {
    ...requiredValidation("Git Account"),
  },
  repositoryUrl: {
    ...requiredValidation("Repository URL"),
    ...patternValidation(httpsRegex, "Please enter a valid HTTPS URL"),
  },
};

export const urlDataSearch: any = {
  url: {
    ...requiredValidation("URL"),
  },
};

export const createRunEnvironmentOptions: any = {
  name: {
    ...requiredValidation("Profile name"),
  },
};

export const createMetadataFieldOptions: any = {
  collectionID: {
    ...requiredValidation("Collection"),
  },
  name: {
    ...requiredValidation("Field name"),
  },
  label: {
    ...requiredValidation("Field label"),
  },
  type: {
    ...requiredValidation("Field type"),
  },
};

export const credentialOptions: any = {
  amazonCredentials: {
    name: {
      ...requiredValidation("Credential Name"),
    },
    amzAccKey: {
      ...requiredValidation("Access Key"),
    },
    amzSucKey: {
      ...requiredValidation("Secret Key"),
    },
    amzDefReg: {
      ...requiredValidation("Region"),
    },
  },
  googleCredentials: {
    name: {
      ...requiredValidation("Credential Name"),
    },
    projectId: {
      ...requiredValidation("Project Id"),
    },
    keyName: {
      ...requiredValidation("Credential File"),
    },
  },
  sshCredentials: {
    name: {
      ...requiredValidation("Credential Name"),
    },
    privateKey: {
      ...requiredValidation("Private Key"),
    },
  },
};

export const addPipelineToProjectOptions: any = {
  projectId: {
    ...requiredValidation("Project"),
  },
  pipelineId: {
    ...requiredValidation("Pipeline"),
  },
};

export const createUserGroupOptions: any = {
  name: {
    ...requiredValidation("User group name"),
  },
};

export const cloudConnectionOptions: any = {
  savedCredentialId: {
    ...requiredValidation("Credential Selection"),
  },
  dataSourceName: {
    ...requiredValidation("Data Source Name"),
  },
  bucketName: {
    ...requiredValidation("Bucket Name"),
  },
};

export const datasourceOptions: any = {
  path: {
    ...requiredValidation("Data Source Path"),
    validate: combineValidations(containsSpaceValidation()),
  },
};

export const addUserGroupMemberOptions: any = {
  email: {
    ...requiredValidation("Email"),
    ...patternValidation(emailRegex, "Invalid email format"),
  },
};

export const sshDataConnectionOptions: any = {
  runEnvId: {
    ...requiredValidation("Run Environment Selection"),
  },
  dataSourceName: {
    ...requiredValidation("Data Source Name"),
  },
  dir: {
    ...requiredValidation("Path"),
  },
};

export const spreadSheetColumnNameOptions: any = {
  name: {
    ...requiredValidation("Column Name"),
  },
};

export const tokenBasedConnectionOptions: any = {
  tokenId: {
    ...requiredValidation("Token"),
  },
  dataSourceName: {
    ...requiredValidation("Data Source Name"),
  },
};

export const PASSWORD_RULES = [
  {
    key: "hasUppercase",
    message: "At least one uppercase",
    pattern: /[A-Z]/,
  },
  {
    key: "hasLowercase",
    message: "At least one lowercase",
    pattern: /[a-z]/,
  },
  {
    key: "hasDigit",
    message: "At least one digit",
    pattern: /\d/,
  },
  {
    key: "hasSpecialCharacter",
    message: "At least one special character",
    pattern: /[\W_]/,
  },
];

export const PASSWORD_MIN_LENGTH = 12;

export const passwordValidationOptions = {
  password: {
    ...requiredValidation("Password"),
    ...minLengthValidation(
      PASSWORD_MIN_LENGTH,
      `${PASSWORD_MIN_LENGTH} characters minimum`
    ),
    ...maxLengthValidation(64, "Password must have at most 64 characters"),
    validate: PASSWORD_RULES.reduce(
      (acc, rule) => {
        acc[rule.key] = (value: string) =>
          rule.pattern.test(value) || rule.message;
        return acc;
      },
      {} as { [key: string]: (value: string) => boolean | string }
    ),
  },
};

export const launcWorkbenchOptions: any = {
  instanceType: {
    ...requiredValidation("Instance Type"),
  },
  diskSize: {
    ...requiredValidation("Disk Size"),
    validate: {
      min: minValueValidation(100, "Disk Size must be at least 100GB"),
      max: maxValueValidation(4096, "Disk Size must be at most 4096GB"),
      noLeadingZeros: (value: string | number) => {
        if (/^0\d+/.test(value.toString())) {
          return "Disk Size cannot have leading zeros";
        }
        return true;
      },
      noDecimalPoints: (value: string | number) => {
        if (value.toString().includes(".")) {
          return "Disk Size cannot have decimal points";
        }
        return true;
      },
    },
  },
  ...passwordValidationOptions,
};

export const createUserFormValidationOptions = {
  password: {
    ...requiredValidation("Password"),
    ...minLengthValidation(8, "Password must have at least 8 characters"),
  },
  confirmPassword: {
    ...requiredValidation("Confirmation Password"),
  },
};

export const autoMergeFormValidationOptions = {
  autoMergeInput: {
    ...requiredValidation("Auto Merge Pattern"),
    validate: {
      hasNameInside: (value: string) => {
        if (!value.includes("{name}")) {
          return "Pattern must include {name}";
        }
        return true;
      },
    },
  },
};

export const urlValidation = (message = "Please enter a valid URL") => ({
  validate: {
    isUrl: (value: string) => isValidUrl(value) || message,
  },
});

export const jsonConfigValidation = (fieldName = "JSON Configuration") => ({
  required: (value: string) => {
    const trimmed = value?.trim();
    if (!trimmed || trimmed === "{}") {
      return `${fieldName} is required`;
    }
    return true;
  },
  validJson: (value: string) => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return "Invalid JSON. Please fix the syntax errors";
    }
  },
});

export const mcpServerOptions: any = {
  name: {
    ...requiredValidation("Name"),
  },
  serverUrl: {
    ...requiredValidation("Server URL"),
    ...urlValidation(),
  },
  transportType: {
    ...requiredValidation("Transport Type"),
  },
  advancedConfig: {
    validate: jsonConfigValidation(),
  },
};

export const userInvitationOptions: any = {
  email: {
    ...patternValidation(emailRegex, "Invalid email format"),
  },
};

export const editOrganizationMarginOptions: any = {
  margin: {
    ...requiredValidation("Margin"),
    validate: {
      min: minValueValidation(0, "Margin must be at least 0"),
      noLeadingZeros: (value: string | number) => {
        if (/^0\d+/.test(value.toString())) {
          return "Margin cannot have leading zeros";
        }
        return true;
      },
    },
  },
};

export const objectValidation = (message = "Must be a valid object") => ({
  validate: {
    isValidJsonObject: (value: string) => {
      if (!value) return true;
      try {
        const parsed = JSON.parse(value);
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          !Array.isArray(parsed)
        )
          return true;
        return message;
      } catch {
        return message;
      }
    },
  },
});

export const arrayValidation = (message = "Must be a valid array") => ({
  validate: {
    isValidArray: (value: string) => {
      if (!value) return true;
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return true;
        return message;
      } catch {
        return message;
      }
    },
  },
});

export const accessCodeOptions: any = {
  code: {
    ...requiredValidation("Code"),
    ...minLengthValidation(5, "Code must be at least 5 characters"),
    ...maxLengthValidation(255, "Code must be at most 255 characters"),
  },
  expiresAt: {
    ...requiredValidation("Expiration date", false),
  },
};

export const healthCheckOptions: any = {
  toEmail: {
    ...requiredValidation("Recipient email"),
    ...patternValidation(emailRegex, "Invalid email format"),
    ...maxLengthValidation(254, "Email must not exceed 254 characters"),
    validate: {
      localPartMax: (value: string) => {
        const [local = ""] = value.split("@");
        return (
          local.length <= 64 ||
          "Email local-part (before @) must not exceed 64 characters"
        );
      },
      domainMax: (value: string) => {
        const [, domain = ""] = value.split("@");
        return (
          domain.length <= 255 ||
          "Email domain (after @) must not exceed 255 characters"
        );
      },
    },
  },
};

export const isValidRoleArn = (value: string): boolean =>
  AWS_ROLE_ARN_RE.test((value ?? "").trim());

export const roleArnValidation = (fieldName = "Role ARN") => ({
  required: `${fieldName} is required`,
  validate: (value: string) =>
    isValidRoleArn(value) ||
    `${fieldName} must look like arn:aws:iam::<account>:role/<name>`,
});

// Deliberately permissive: an s3:// or gs:// URI, or an absolute filesystem
// path. The server is the source of truth for what an org actually allows.
const DIR_PREFIX_RE = /^(?:s3:\/\/|gs:\/\/|\/)\S+$/;

export const isValidDirPrefix = (value: string): boolean =>
  DIR_PREFIX_RE.test((value ?? "").trim());

export const dirPrefixValidation = (fieldName = "Path prefix") => ({
  required: `${fieldName} is required`,
  validate: (value: string) =>
    isValidDirPrefix(value) ||
    `${fieldName} must be an absolute path, or start with s3:// or gs://`,
});
